// src/components/MonthGridCalendar.jsx
import React, { useMemo } from "react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function startOfMonth(year, monthIndex) {
  return new Date(year, monthIndex, 1);
}
function endOfMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0);
}

export default function MonthGridCalendar({
  year,
  monthIndex, // 0 = Jan
  eventsByDate = {}, // { "YYYY-MM-DD": [{ title, color, href }] }
  title
}) {
  const data = useMemo(() => {
    const first = startOfMonth(year, monthIndex);
    const last = endOfMonth(year, monthIndex);

    const monthName = first.toLocaleString(undefined, { month: "long" });
    const label = title || `${monthName} ${year}`;

    const leadingEmpty = first.getDay(); // 0..6
    const daysInMonth = last.getDate();

    const cells = [];

    // leading blanks
    for (let i = 0; i < leadingEmpty; i++) {
      cells.push({ type: "blank", key: `b-${i}` });
    }

    // days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthIndex, day);
      const key = ymd(date);
      const isToday = key === ymd(new Date());
      cells.push({
        type: "day",
        key,
        day,
        dateKey: key,
        isToday,
        events: eventsByDate[key] || []
      });
    }

    return { label, cells };
  }, [year, monthIndex, eventsByDate, title]);

  return (
    <section className="q-calendar">
      <div className="q-calendar__top">
        <h2 className="q-calendar__title">{data.label}</h2>
      </div>

      <div className="q-calendar__weekdays">
        {WEEKDAYS.map((w) => (
          <div key={w} className="q-calendar__weekday">
            {w}
          </div>
        ))}
      </div>

      <div className="q-calendar__grid" role="grid" aria-label={data.label}>
        {data.cells.map((c) => {
          if (c.type === "blank") {
            return <div key={c.key} className="q-calendar__cell q-calendar__cell--blank" />;
          }

          return (
            <div
              key={c.key}
              className={`q-calendar__cell ${c.isToday ? "q-calendar__cell--today" : ""}`}
              role="gridcell"
              aria-label={c.dateKey}
            >
              <div className="q-calendar__date">{c.day}</div>

              {c.events.length > 0 && (
                <div className="q-calendar__events">
                  {c.events.slice(0, 3).map((ev, idx) => {
                    const Dot = (
                      <span
                        className="q-calendar__dot"
                        style={{ background: ev.color || "rgba(99,102,241,0.9)" }}
                        aria-hidden="true"
                      />
                    );

                    const content = (
                      <>
                        {Dot}
                        <span className="q-calendar__eventTitle">{ev.title}</span>
                      </>
                    );

                    return ev.href ? (
                      <a
                        key={idx}
                        className="q-calendar__event"
                        href={ev.href}
                        target={ev.href.startsWith("/#/") ? undefined : "_blank"}
                        rel={ev.href.startsWith("/#/") ? undefined : "noopener noreferrer"}
                        title={ev.title}
                      >
                        {content}
                      </a>
                    ) : (
                      <div key={idx} className="q-calendar__event" title={ev.title}>
                        {content}
                      </div>
                    );
                  })}
                  {c.events.length > 3 && (
                    <div className="q-calendar__more">+{c.events.length - 3} more</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}